"use client";
import SectionWrapper, { SectionWrapperProps } from "@cc/section-wrapper";
import {
  alpha,
  Divider,
  Stack,
  StackProps,
  Typography,
  TypographyProps,
} from "@mui/material";
import { H3 } from "@theme/components/typography.fontvariant";
import { MaterialImage } from "@cc/material-components";
import { deepmerge } from "@mui/utils";
import { StaticImageData } from "next/image";
import { ReactNode } from "react";
import BorderedIconButton from "@cc/bordered-icon-button";
import {
  FileDownloadOutlined,
  HeadphonesOutlined,
  LocationOn,
  ShareOutlined,
} from "@mui/icons-material";
import ArrowGradient from "@cc/arrow-gradient";
import { sxArrayUtil } from "@util/sx-helpers";
import { arrowTransition } from "@c/home/social-section";
import {
  MotionImage,
  MotionImageProps,
  MotionStack,
  MotionTypography,
  MotionTypographyProps,
} from "./motion-components";
import {
  bottomToToptextStagger,
  bottomToToptextStaggerChildren,
} from "./animations";

export interface BriefDetailInfoProps {
  tags?: string[];
  TagsTypographyProps?: Omit<TypographyProps, "children">;
  location?: string;
  LocationTypographyProps?: Omit<MotionTypographyProps, "children">;
  title: string;
  TitleTypographyProps?: Omit<MotionTypographyProps, "children">;
  date?: string;
  DateTypographyProps?: Omit<TypographyProps, "children">;
  timeToRead?: string;
  TimeToReadTypographyProps?: Omit<TypographyProps, "children">;
  banner?: string | StaticImageData;
  BannerImageProps?: Omit<Partial<MotionImageProps>, "children" | "src">;
  SectionWrapperProps?: Omit<SectionWrapperProps, "children">;
  RootStackWrapperProps?: Omit<StackProps, "children">;
  children?: ReactNode;
  newsBy?: {
    name: string;
    image?: string | StaticImageData;
  };
  isDownloadable?: boolean;
  isAudio?: boolean;
  isShareable?: boolean;
  removeOptions?: boolean;
  description?: string;
}

const BriefDetailInfo = (props: BriefDetailInfoProps) => {
  const {
    tags = [],
    TagsTypographyProps,
    title,
    TitleTypographyProps,
    location,
    LocationTypographyProps,
    date,
    DateTypographyProps,
    timeToRead,
    TimeToReadTypographyProps,
    banner,
    BannerImageProps,
    children,
    SectionWrapperProps,
    RootStackWrapperProps,
    removeOptions,
    newsBy,
    isDownloadable,
    isAudio,
    isShareable,
  } = props;

  return (
    <Stack {...RootStackWrapperProps}>
      <SectionWrapper
        {...deepmerge(defaultSectionWrapperProps, SectionWrapperProps)}
      >
        <MotionStack
          maxWidth={1086}
          width={1}
          mx={"auto"}
          rowGap={{ xs: 2, md: 3 }}
          position={"relative"}
          variants={bottomToToptextStagger()}
          initial="initial"
          whileInView={"animate"}
          viewport={{ once: true }}
        >
          <ArrowGradient
            SVGProps={{
              width: 110,
              height: 280,
            }}
            sx={{
              display: { xs: "none", xl_xxl: "block" },
              position: "absolute",
              top: -54,
              right: -116,
            }}
            {...arrowTransition}
          />
          <MotionStack
            direction={"row"}
            columnGap={3}
            width={"fit-content"}
            variants={bottomToToptextStaggerChildren}
          >
            {tags.map((tag, index) => {
              return (
                <Typography
                  textTransform={"capitalize"}
                  key={`${tag}-${index}`}
                  {...deepmerge(tagDefaultProps, TagsTypographyProps)}
                >
                  {tag}
                </Typography>
              );
            })}
            {location && (
              <MotionTypography
                variant={"body2"}
                {...LocationTypographyProps}
                sx={[
                  {
                    display: { xs: "none", md: "flex" },
                    alignItems: "center",
                    columnGap: 1,
                  },
                  ...sxArrayUtil(LocationTypographyProps?.sx),
                ]}
                variants={bottomToToptextStaggerChildren}
              >
                <LocationOn fontSize={"small"} />
                {location}
              </MotionTypography>
            )}
          </MotionStack>
          <MotionTypography
            variant="h1"
            fontSize={H3}
            {...TitleTypographyProps}
            variants={bottomToToptextStaggerChildren}
          >
            {title}
          </MotionTypography>
          {location && (
            <MotionTypography
              variant={"body2"}
              {...LocationTypographyProps}
              sx={[
                {
                  display: { xs: "flex", md: "none" },
                  alignItems: "center",
                  columnGap: 1,
                },
                ...sxArrayUtil(LocationTypographyProps?.sx),
              ]}
              variants={bottomToToptextStaggerChildren}
            >
              <LocationOn fontSize={"small"} />
              {location}
            </MotionTypography>
          )}
          <MotionStack
            direction={{ md: "row" }}
            alignItems={{ md: "center" }}
            justifyContent={"space-between"}
            rowGap={4}
            variants={bottomToToptextStaggerChildren}
          >
            {(date || timeToRead || newsBy || !removeOptions) && (
              <Stack
                direction={"row"}
                width={"fit-content"}
                columnGap={{ xs: 1.5, md: 2 }}
                divider={
                  <Divider
                    flexItem
                    orientation={"vertical"}
                    sx={{
                      borderColor: (theme) =>
                        alpha(theme.palette.common.white, 0.3),
                    }}
                  />
                }
                color={(theme) => alpha(theme.palette.common.white, 0.7)}
              >
                {newsBy && (
                  <Typography
                    variant={"subtitle2"}
                    display={"flex"}
                    alignItems={"center"}
                    columnGap={1}
                  >
                    {newsBy.image && (
                      <MaterialImage
                        src={newsBy.image}
                        alt={newsBy.name}
                        width={24}
                        height={11}
                        sx={{
                          width: 24,
                          height: 11,
                        }}
                      />
                    )}
                    {newsBy.name}
                  </Typography>
                )}
                {date && (
                  <Typography variant={"subtitle2"} {...DateTypographyProps}>
                    {date}
                  </Typography>
                )}
                {timeToRead && (
                  <Typography
                    variant={"subtitle2"}
                    {...TimeToReadTypographyProps}
                  >
                    {timeToRead}
                  </Typography>
                )}
              </Stack>
            )}
            {(isDownloadable || isAudio || isShareable) && !removeOptions && (
              <MotionStack
                ml={"auto"}
                direction={"row"}
                columnGap={4}
                justifyContent={{ md: "space-between" }}
                alignItems={"center"}
                width={{ xs: 1, md: "fit-content" }}
                variants={bottomToToptextStaggerChildren}
              >
                {isDownloadable && (
                  <Typography {...actionTypographyDefaultProps}>
                    <BorderedIconButton size={"small"} color={"secondary"}>
                      <FileDownloadOutlined
                        fontSize={"small"}
                        color={"secondary"}
                      />
                    </BorderedIconButton>
                    Download
                  </Typography>
                )}
                {isAudio && (
                  <Typography {...actionTypographyDefaultProps}>
                    <BorderedIconButton size={"small"} color={"secondary"}>
                      <HeadphonesOutlined fontSize={"small"} />
                    </BorderedIconButton>
                    Audio
                  </Typography>
                )}
                {isShareable && (
                  <Typography {...actionTypographyDefaultProps}>
                    <BorderedIconButton size={"small"} color={"secondary"}>
                      <ShareOutlined fontSize={"small"} />
                    </BorderedIconButton>
                    Share
                  </Typography>
                )}
              </MotionStack>
            )}
          </MotionStack>
          {banner && (
            <MotionImage
              src={banner}
              alt={title}
              {...deepmerge(blogsBannerDefaultProps, BannerImageProps)}
              variants={bottomToToptextStaggerChildren}
            />
          )}
          {children}
        </MotionStack>
      </SectionWrapper>
    </Stack>
  );
};

export default BriefDetailInfo;

const defaultSectionWrapperProps: BriefDetailInfoProps["SectionWrapperProps"] =
  {
    SectionProps: {
      sx: {
        position: "relative",
        overflow: "visible",
        mb: { xs: 14, md: 30 },
        "&::before": {
          content: "''",
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: { xs: 400, md: 400, lg: 526 },
          background: (theme) => theme.palette.primary.main,
          zIndex: 0,
        },
      },
    },
    sx: {
      position: "relative",
      pt: { xxl: 10, md: 4, xs: 4 },
      pb: { xs: 5, md: 6, xxl: 7 },
      mb: { xs: -14, md: -30 },
    },
  };

const tagDefaultProps: BriefDetailInfoProps["TagsTypographyProps"] = {
  variant: "body2",
  sx: {
    px: 2,
    py: { xs: 0.75, md: 1.5 },
    background: (theme) => alpha(theme.palette.common.white, 0.2),
  },
};

export const blogsBannerDefaultProps: BriefDetailInfoProps["BannerImageProps"] =
  {
    width: 1090,
    height: 388,
    sx: {
      my: { xs: 3, md: 4 },
      width: 1,
      height: { xs: 275, md: "auto" },
      aspectRatio: "25/9",
      objectFit: "cover",
      objectPosition: "center",
      zIndex: 1,
    },
  };

const actionTypographyDefaultProps: Omit<TypographyProps, "children"> = {
  variant: "subtitle2",
  sx: {
    display: "flex",
    columnGap: 1,
    width: "fit-content",
    textTransform: "Capitalize",
    alignItems: "center",
  },
};
